import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { VerdictBadge } from "@/components/RiskBadge";
import BackButton from "@/components/BackButton";
import { GitCompareArrows, Clock, Layers } from "lucide-react";

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/sessions").then(({ data }) => setSessions(data)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <div>
      <BackButton />
      <div className="mb-8">
        <p className="eyebrow mb-2">INTERVIEW TIMELINES</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Sessions</h1>
        <p className="text-slate-400 text-sm mt-2">
          Each session groups a candidate's baseline scan with every mid-interview re-scan.
        </p>
      </div>

      <div className="card-soc overflow-hidden">
        {sessions.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">No sessions yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead>
              <tr className="text-left eyebrow border-b border-white/8">
                <th className="px-6 py-3 font-normal">Candidate</th>
                <th className="px-6 py-3 font-normal">Interview</th>
                <th className="px-6 py-3 font-normal">Scans</th>
                <th className="px-6 py-3 font-normal">Baseline → Latest</th>
                <th className="px-6 py-3 font-normal">Environment</th>
                <th className="px-6 py-3 font-normal">Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.session_id}
                  onClick={() => navigate(`/sessions/${s.session_id}`)}
                  data-testid={`session-row-${s.session_id}`}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-3.5">
                    <div className="text-slate-100 font-medium">{s.candidate_name}</div>
                    <div className="text-xs text-slate-500">{s.candidate_email || "—"}</div>
                  </td>
                  <td className="px-6 py-3.5 text-slate-400 font-mono text-xs">{s.interview_id || "—"}</td>
                  <td className="px-6 py-3.5">
                    <span className="flex items-center gap-1.5 text-slate-300">
                      <Layers className="w-3.5 h-3.5 text-slate-500" /> {s.scan_count}
                      {s.pending_tokens > 0 && (
                        <span className="text-[10px] font-mono text-sky-400 ml-1">+{s.pending_tokens} pending</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-3.5">
                    {s.scan_count === 0 ? (
                      <span className="text-xs text-slate-500 font-mono">awaiting scan</span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <VerdictBadge verdict={s.baseline_verdict} />
                        {s.scan_count > 1 && (
                          <>
                            <span className="text-slate-600">→</span>
                            <VerdictBadge verdict={s.latest_verdict} />
                          </>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3.5">
                    {s.environment_changed ? (
                      <span className="flex items-center gap-1.5 text-xs font-mono text-red-400">
                        <GitCompareArrows className="w-3.5 h-3.5" /> CHANGED
                      </span>
                    ) : s.scan_count > 1 ? (
                      <span className="text-xs font-mono text-emerald-400">STABLE</span>
                    ) : (
                      <span className="text-xs font-mono text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3.5 text-slate-500 text-xs">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {new Date(s.last_activity).toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
